import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Eye, EyeOff, CheckCircle2, ShieldCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { logPasswordResetEvent } from "@/lib/passwordResetTelemetry";
import logo from "@/assets/logo-chillwerk.png";

const passwordSchema = z.string()
  .min(8, "Wachtwoord moet minimaal 8 karakters zijn")
  .regex(/[a-z]/, "Wachtwoord moet minimaal één kleine letter bevatten")
  .regex(/[A-Z]/, "Wachtwoord moet minimaal één hoofdletter bevatten")
  .regex(/[0-9]/, "Wachtwoord moet minimaal één cijfer bevatten")
  .regex(/[^a-zA-Z0-9]/, "Wachtwoord moet minimaal één speciaal teken bevatten");

export const ResetPasswordForm = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { clearPasswordRecovery } = useAuth();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const result = passwordSchema.safeParse(password);
    if (!result.success) {
      setError(result.error.errors[0].message);
      return;
    }
    if (password !== confirm) {
      setError("Wachtwoorden komen niet overeen");
      return;
    }

    setLoading(true);
    const { data: updateData, error: updateError } = await supabase.auth.updateUser({ password });
    setLoading(false);

    if (updateError) {
      const raw = updateError.message || "";
      const message = /different from the old password/i.test(raw)
        ? "Dit is je huidige wachtwoord. Kies een nieuw, ander wachtwoord."
        : raw;
      setError(message);
      toast({
        title: "Wachtwoord wijzigen mislukt",
        description: message,
        variant: "destructive",
      });
      return;
    }


    setSuccess(true);
    void logPasswordResetEvent({ event_type: "completed" });

    if (updateData?.user?.id) {
      void supabase
        .from("profiles")
        .update({ must_change_password: false })
        .eq("id", updateData.user.id);
    }

    // Fire-and-forget bevestigingsmail
    const confirmEmail = updateData?.user?.email;
    if (confirmEmail) {
      void supabase.functions.invoke("send-password-changed", {
        body: { email: confirmEmail, context: "reset" },
      });
    }

    if (confirmEmail && !updateData?.user?.email_confirmed_at) {
      clearPasswordRecovery();
      await supabase.auth.signOut();
      toast({
        title: "Wachtwoord gewijzigd! 🔒",
        description: "Bevestig nu je e-mailadres om in te loggen.",
      });
      setTimeout(() => {
        navigate(`/auth?verifyEmail=${encodeURIComponent(confirmEmail)}`, { replace: true });
      }, 1200);
      return;
    }

    // Clear recovery state before routing. Do not rewrite the URL to /auth here:
    // that briefly showed the login/reset screen again before the dashboard redirect.
    clearPasswordRecovery();

    // Auto-login flow: keep the recovery-elevated session and route into the dashboard.
    // Fallback: if for any reason there's no active session, send the user to /auth
    // with their email prefilled so they only have to type the new password.
    const userEmail = updateData?.user?.email ?? null;

    try {
      const { data: sessionData } = await supabase.auth.getSession();

      if (!sessionData?.session) {
        toast({
          title: "Wachtwoord gewijzigd! 🔒",
          description: "Log opnieuw in met je nieuwe wachtwoord.",
        });
        setTimeout(() => {
          const qs = userEmail ? `?email=${encodeURIComponent(userEmail)}` : "";
          navigate(`/auth${qs}`, { replace: true });
        }, 1500);
        return;
      }

      // Determine role-based redirect, with safe default to candidate dashboard.
      let target = "/dashboard/kandidaat";
      try {
        const userId = sessionData.session.user.id;
        const { data: profile } = await supabase
          .from("profiles")
          .select("user_type")
          .eq("id", userId)
          .maybeSingle();
        if (profile?.user_type === "company") {
          target = "/bedrijf/control-room";
        }
      } catch {
        // Role lookup failed — keep default candidate dashboard.
      }

      toast({
        title: "Wachtwoord gewijzigd! 🎉",
        description: "Je bent automatisch ingelogd.",
      });

      setTimeout(() => {
        navigate(target, { replace: true });
      }, 1200);
    } catch {
      // Last-resort fallback: route to /auth with prefilled email.
      toast({
        title: "Wachtwoord gewijzigd! 🔒",
        description: "Log opnieuw in met je nieuwe wachtwoord.",
      });
      setTimeout(() => {
        const qs = userEmail ? `?email=${encodeURIComponent(userEmail)}` : "";
        navigate(`/auth${qs}`, { replace: true });
      }, 1500);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-cyan-light to-white flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <img src={logo} alt="ChillWerk Logo" className="h-20 mx-auto mb-4" />
          </div>
          <Card className="shadow-lg border-2">
            <CardContent className="pt-8 pb-8 flex flex-col items-center text-center gap-4">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="w-9 h-9 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-heading font-black">Wachtwoord gewijzigd!</h2>
          <p className="text-muted-foreground">Je wordt doorgestuurd naar je dashboard...</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-cyan-light to-white flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <img src={logo} alt="ChillWerk Logo" className="h-20 mx-auto mb-4" />
        </div>

        <Card className="shadow-lg border-2">
          <CardHeader className="text-center">
            <div className="w-12 h-12 rounded-full bg-cyan/15 mx-auto mb-2 flex items-center justify-center">
              <ShieldCheck className="w-6 h-6 text-cyan-dark" />
            </div>
            <CardTitle className="text-2xl font-heading font-black">
              Nieuw wachtwoord instellen
            </CardTitle>
            <CardDescription>
              Kies een sterk wachtwoord om je account weer veilig te maken.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-password" className="font-bold">Nieuw wachtwoord</Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={show ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-12 rounded-xl border-2 pr-10"
                    required
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShow(!show)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label={show ? "Verberg wachtwoord" : "Toon wachtwoord"}
                  >
                    {show ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password" className="font-bold">Bevestig wachtwoord</Label>
                <Input
                  id="confirm-password"
                  type={show ? "text" : "password"}
                  placeholder="••••••••"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="h-12 rounded-xl border-2"
                  required
                />
              </div>

              <div className="text-xs text-muted-foreground bg-muted/50 rounded-lg p-3 space-y-1">
                <p className="font-semibold text-foreground">Wachtwoordeisen:</p>
                <ul className="list-disc list-inside space-y-0.5">
                  <li>Minimaal 8 karakters</li>
                  <li>Minimaal één hoofdletter en één kleine letter</li>
                  <li>Minimaal één cijfer</li>
                  <li>Minimaal één speciaal teken</li>
                </ul>
              </div>

              {error && (
                <p className="text-sm text-destructive font-medium">{error}</p>
              )}

              <Button
                type="submit"
                variant="cta"
                size="lg"
                className="w-full h-12 text-base font-black rounded-xl"
                disabled={loading}
              >
                {loading ? "Wachtwoord opslaan..." : "Wachtwoord opslaan"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
